/**
 * KAN-958: настройки интерфейса пользователя (services/api/uiSettings).
 * update меняет кеш сразу (оптимистично), при ошибке возвращает прежнее значение,
 * после ответа кладёт в кеш то, что вернул сервер.
 * Пока настройки не пришли или запрос упал: пустые настройки (всё по умолчанию).
 */
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { uiSettingsApi, type UiSettings } from '@/services/api/uiSettings';

export const UI_SETTINGS_QUERY_KEY = ['ui-settings'] as const;

const EMPTY: UiSettings = {};

export interface UiSettingsState {
  settings: UiSettings;
  isLoading: boolean;
  update: (patch: Partial<UiSettings>) => void;
}

export function useUiSettings(): UiSettingsState {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: UI_SETTINGS_QUERY_KEY,
    queryFn: uiSettingsApi.get,
    staleTime: Infinity,
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: (patch: Partial<UiSettings>) => uiSettingsApi.patch(patch),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: UI_SETTINGS_QUERY_KEY });
      const previous = queryClient.getQueryData<UiSettings>(UI_SETTINGS_QUERY_KEY);
      queryClient.setQueryData<UiSettings>(UI_SETTINGS_QUERY_KEY, { ...(previous ?? EMPTY), ...patch });
      return { previous };
    },
    onError: (_error, _patch, context) => {
      queryClient.setQueryData(UI_SETTINGS_QUERY_KEY, context?.previous ?? EMPTY);
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(UI_SETTINGS_QUERY_KEY, saved);
    },
  });

  const { mutate } = mutation;
  const update = useCallback((patch: Partial<UiSettings>) => mutate(patch), [mutate]);

  return { settings: query.data ?? EMPTY, isLoading: query.isLoading, update };
}
